export default {
  data() {
    return {
      hasTeacherLink: false,
      teacherScopeTID: null,
      teacherClassesData: [],
      assignedClassIDs: [],
      allSubjects: [],
      teacherScopedListCache: null,
    };
  },
  computed: {
    isTeacherScoped() {
      return this.hasTeacherLink && !!this.teacherScopeTID;
    },
    plannerScopeCampusID() {
      const domain = (this.$store.state.report || "").replace(/_/g, "");
      return domain === "myskooliuss" ? 1 : this.$store.state.userData.cId;
    },
  },
  methods: {
    getCurrentUserLinkID() {
      return (
        this.$store.state.userData.userID ||
        this.$store.state.userData.localID ||
        this.$store.state.userData.userLinkID ||
        0
      );
    },

    extractStaffIdFromResponse(staffResponse) {
      if (staffResponse && staffResponse.id) {
        return staffResponse.id;
      }
      if (staffResponse && staffResponse.data && staffResponse.data.id) {
        return staffResponse.data.id;
      }
      if (
        staffResponse &&
        Array.isArray(staffResponse) &&
        staffResponse.length > 0 &&
        staffResponse[0].id
      ) {
        return staffResponse[0].id;
      }
      if (
        staffResponse &&
        Array.isArray(staffResponse.data) &&
        staffResponse.data.length > 0 &&
        staffResponse.data[0].id
      ) {
        return staffResponse.data[0].id;
      }
      return null;
    },

    async loadStaffIdForCurrentUser() {
      try {
        const userLinkID = this.getCurrentUserLinkID();
        const cID = this.plannerScopeCampusID;
        const dbName = encodeURIComponent(this.$store.state.userData.db);

        if (!userLinkID || !cID) {
          this.hasTeacherLink = false;
          this.teacherScopeTID = null;
          return;
        }

        const staffUrl = `${this.$store.state.domain}Staff/GetByUserLinkID?db=${dbName}&userLinkID=${userLinkID}&cID=${cID}`;
        const staffResponse = await this.get({
          url: staffUrl,
          token: this.$store.state.userData.token,
        });

        const staffID = this.extractStaffIdFromResponse(staffResponse);
        if (staffID) {
          this.hasTeacherLink = true;
          this.teacherScopeTID = staffID;
        } else {
          this.hasTeacherLink = false;
          this.teacherScopeTID = null;
        }
      } catch (error) {
        console.error("Error loading staff ID for planner scope:", error);
        this.hasTeacherLink = false;
        this.teacherScopeTID = null;
      }
    },

    async initTeacherPlannerScope() {
      await this.loadStaffIdForCurrentUser();
      if (this.isTeacherScoped) {
        await this.loadTeacherClasses();
      }
    },

    parseTeacherClassesResponse(response) {
      if (response && response.data && Array.isArray(response.data)) {
        return response.data;
      }
      if (Array.isArray(response)) {
        return response;
      }
      if (response && response.status === "success" && response.data) {
        return Array.isArray(response.data) ? response.data : [];
      }
      return [];
    },

    getTeacherClassId(classItem) {
      return classItem.cID ?? classItem.clsID ?? classItem.classID ?? null;
    },

    async loadTeacherClasses() {
      if (!this.teacherScopeTID) {
        this.teacherClassesData = [];
        this.assignedClassIDs = [];
        return [];
      }

      try {
        const dbName = encodeURIComponent(this.$store.state.userData.db);
        const url = `${this.$store.state.domain}teacherClasses/LoadData?db=${dbName}&tID=${this.teacherScopeTID}&cID=${this.plannerScopeCampusID}`;
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });

        const data = this.parseTeacherClassesResponse(response);
        this.teacherClassesData = data;
        const classMap = new Map();
        data.forEach((classItem) => {
          const classId = this.getTeacherClassId(classItem);
          if (classId == null || classMap.has(classId)) return;
          classMap.set(classId, {
            id: classId,
            name:
              classItem.className ||
              classItem.name ||
              classItem.cls?.name ||
              `Class ${classId}`,
          });
        });
        this.assignedClassIDs = Array.from(classMap.keys());
        return Array.from(classMap.values());
      } catch (error) {
        console.error("Error loading teacher classes:", error);
        this.teacherClassesData = [];
        this.assignedClassIDs = [];
        return [];
      }
    },

    normalizeTeacherClassesToLoadFormat(data = this.teacherClassesData) {
      return (data || []).map((classItem) => {
        const classId = this.getTeacherClassId(classItem);
        const sections = [];
        (classItem.secSubjectsData || []).forEach((sec) => {
          if (sec.sectionID != null) {
            sections.push({
              id: sec.sectionID,
              section:
                sec.sectionName ||
                sec.section ||
                `Section ${sec.sectionID}`,
            });
          }
        });
        return {
          cls: {
            id: classId,
            name:
              classItem.className ||
              classItem.name ||
              `Class ${classId}`,
          },
          sections,
          secSubjectsData: classItem.secSubjectsData || [],
        };
      });
    },

    getTeacherClassItem(classID) {
      if (!classID) return null;
      return this.teacherClassesData.find((classItem) => {
        const classId = this.getTeacherClassId(classItem);
        return Number(classId) === Number(classID);
      });
    },

    getAssignedSubjectsForClass(classID, sectionID = null) {
      if (!this.isTeacherScoped || !classID) return [];

      const classItem = this.getTeacherClassItem(classID);
      const subjects = new Map();
      (classItem?.secSubjectsData || []).forEach((section) => {
        if (
          sectionID != null &&
          sectionID !== 0 &&
          Number(section.sectionID) !== Number(sectionID)
        ) {
          return;
        }
        (section.subjects || []).forEach((sub) => {
          if (sub?.id != null && !subjects.has(sub.id)) {
            subjects.set(sub.id, {
              id: sub.id,
              subject: sub.subject || sub.name || `Subject ${sub.id}`,
            });
          }
        });
      });
      return Array.from(subjects.values());
    },

    getAssignedSubjectIDsForClass(classID, sectionID = null) {
      return this.getAssignedSubjectsForClass(classID, sectionID).map(
        (sub) => sub.id
      );
    },

    appendTeacherScopeQueryParts(parts, classID = null) {
      if (!this.isTeacherScoped) {
        if (classID) {
          parts.push(`classID=${classID}`);
        } else {
          parts.push("classID=");
        }
        return parts;
      }

      if (classID) {
        parts.push(`classID=${classID}`);
      } else if (this.assignedClassIDs.length > 0) {
        parts.push(`classIDs=${this.assignedClassIDs.join(",")}`);
      }
      parts.push(`tID=${this.teacherScopeTID}`);
      return parts;
    },

    appendTeacherScopeToUrl(url, classID = null) {
      const parts = [];
      this.appendTeacherScopeQueryParts(parts, classID);
      if (!parts.length) return url;
      const separator = url.includes("?") ? "&" : "?";
      return `${url}${separator}${parts.join("&")}`;
    },

    filterRowsForTeacherScope(
      items,
      { classKey = "classID", subjectKey = "subjectID" } = {}
    ) {
      if (!this.isTeacherScoped || !Array.isArray(items)) {
        return items || [];
      }

      return items.filter((item) => {
        const classId = item[classKey] ?? item.classId;
        const subjectId = item[subjectKey] ?? item.subjectId;

        if (
          !this.assignedClassIDs.some(
            (id) => Number(id) === Number(classId)
          )
        ) {
          return false;
        }

        const allowedSubjectIDs = this.getAssignedSubjectIDsForClass(classId);
        return allowedSubjectIDs.some(
          (id) => Number(id) === Number(subjectId)
        );
      });
    },

    async loadAllPlannerSubjects() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.plannerScopeCampusID,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        if (response && response.data && Array.isArray(response.data)) {
          this.allSubjects = response.data;
        } else if (Array.isArray(response)) {
          this.allSubjects = response;
        } else {
          this.allSubjects = [];
        }
        return this.allSubjects;
      } catch (error) {
        console.error("Error loading subjects:", error);
        this.allSubjects = [];
        return [];
      }
    },

    updateScopedSubjects(classID, sectionID = null) {
      if (this.isTeacherScoped) {
        return classID
          ? this.getAssignedSubjectsForClass(classID, sectionID)
          : [];
      }
      return this.allSubjects;
    },

    invalidateTeacherScopedCache() {
      this.teacherScopedListCache = null;
    },

    async fetchTeacherScopedPaginatedData({ cacheKey, fetchPage, filterOptions }) {
      if (!this.isTeacherScoped) {
        return null;
      }

      if (
        this.teacherScopedListCache &&
        this.teacherScopedListCache.key === cacheKey
      ) {
        return this.teacherScopedListCache.items;
      }

      const pageSize = 1000;
      let page = 1;
      let allRaw = [];

      while (page <= 50) {
        const res = await fetchPage(page, pageSize);
        const batch = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
          ? res
          : [];
        const reportedTotal = Number(
          res?.total ?? res?.totalCount ?? res?.totalRecords ?? 0
        );

        allRaw = allRaw.concat(batch);

        if (!batch.length || batch.length < pageSize) {
          break;
        }
        if (reportedTotal && allRaw.length >= reportedTotal) {
          break;
        }
        page += 1;
      }

      const filtered = this.filterRowsForTeacherScope(allRaw, filterOptions);
      this.teacherScopedListCache = { key: cacheKey, items: filtered };
      return filtered;
    },

    getTeacherScopedPageSlice(allItems) {
      const totalCount = Array.isArray(allItems) ? allItems.length : 0;
      const start = (this.currentPage - 1) * this.perPage;
      return {
        items: allItems.slice(start, start + this.perPage),
        totalCount,
      };
    },

    applyTeacherScopedListResult(rawItems, apiTotal, filterOptions) {
      if (!this.isTeacherScoped) {
        return {
          items: rawItems || [],
          totalCount: Number(apiTotal) || (rawItems?.length || 0),
        };
      }

      const filtered = this.filterRowsForTeacherScope(rawItems, filterOptions);
      const rawLen = rawItems?.length || 0;
      const filteredLen = filtered.length;
      const apiTotalNum = Number(apiTotal) || rawLen;

      if (rawLen === filteredLen) {
        return { items: filtered, totalCount: apiTotalNum };
      }

      const removedOnPage = rawLen - filteredLen;
      let totalCount = Math.max(filteredLen, apiTotalNum - removedOnPage);

      if (
        this.currentPage === 1 &&
        filteredLen < this.perPage &&
        rawLen <= this.perPage
      ) {
        totalCount = filteredLen;
      }

      return { items: filtered, totalCount };
    },
  },
};
